import { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  HEADER_CONFIG,
  HEADER_CONST,
} from '../../common/constant/common-constant'
import {
  INVENTORY_STAGES,
  INVENTORY_TABS,
  PAGE_CONST,
  PERMISSION_FEATURE,
  PERMISSION_TYPE,
} from '../../common/constant/page-constant'
import useCheckMobileScreen from '../../common/hooks/use-check-mobile-screen-hook'
import usePagination from '../../common/hooks/usePagination'
import { redirect } from '../../common/utils/redirectionUtils'
import Loading from '../../components/Loding/Loading'
import Pagination from '../../components/PaginationComponent/Pagination'
import {
  getCrossBranchInventoryHelper,
  getInventoryDetailsHelper,
  getInvoiceDetailsHelper,
  getProductDetailsHelper,
} from '../../helper/home-helper'
import { getBranchHelper } from '../../helper/branch-management-helper'
import Card from '../Common/Card/Card'
import MobileHeader from '../Common/Header/MobileHeader'
import ViewInventory from './ViewInventory'
import InventoryFilter from './components/InventoryFilter'

import { formatDate } from './../../common/utils/appUtils'
import currencyFormatter from './../../common/utils/currencyFormatterUtils'
import './ManageInventory.scss'

// Helper function to format product name as: Name (dose)-strength-category
// dose: size value, goes in brackets if exists
// strength: goes after first hyphen
// category: form type (Tablet, Capsule, etc.), goes after second hyphen
const formatProductName = (name, dose, strength, category) => {
  let result = name || ''
  if (dose) {
    result += ` (${dose})`
  }
  if (strength) {
    result += `-${strength}`
  }
  if (category) {
    result += `-${category}`
  }
  return result
}

// Helper function to calculate remaining days from expiry date
const calculateRemainingDays = (expiryDate) => {
  if (!expiryDate) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const expiry = new Date(expiryDate)
  expiry.setHours(0, 0, 0, 0)
  const diffTime = expiry - today
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays
}

const ManageInventory = (props) => {
  const navigate = useNavigate()
  const isMobile = useCheckMobileScreen()

  const [viewDrawer, setViewDrawer] = useState(false)
  const [open, setOpen] = useState(false)
  const [inventoryData, setInventoryData] = useState([])
  const [productData, setProductData] = useState([])
  const [invoiceData, setInvoiceData] = useState(undefined)
  const [activeTab, setActiveTab] = useState(INVENTORY_TABS.PHARAMCEUTICAL)
  const [inventoryStage, setInventoryStage] = useState(
    INVENTORY_STAGES.PRODUCT_LISTING
  )
  const [activeProduct, setActiveProduct] = useState(undefined)

  const [headerOptions, setHeaderOptions] = useState({
    [HEADER_CONFIG.IS_BACK_VISIBLE]: true,
    [HEADER_CONFIG.TITLE]: HEADER_CONST.MANAGE_INVENTORY,
    [HEADER_CONFIG.HANDLE_BACK_FUNC]: () => redirect(navigate, PAGE_CONST.HOME),
  })

  const [inventoryLoading, setInventoryLoading] = useState(false)
  const [filterOptions, setFilterOptions] = useState({
    search: '',
  })
  const [showingAlternatives, setShowingAlternatives] = useState(false)
  const [zeroStockProducts, setZeroStockProducts] = useState([])
  const [reminderFilterDays, setReminderFilterDays] = useState(0)

  // Cross-branch inventory states
  const [showCrossBranchView, setShowCrossBranchView] = useState(false)
  const [branches, setBranches] = useState([])
  const [selectedBranch, setSelectedBranch] = useState(null)
  const [crossBranchInventory, setCrossBranchInventory] = useState([])
  const [crossBranchLoading, setCrossBranchLoading] = useState(false)
  const [crossBranchSearch, setCrossBranchSearch] = useState('')

  // Check if user has cross-branch inventory permission
  const hasCrossBranchPermission = () => {
    // Owner/Admin users have full access
    if (props.loginData?.user_type === 'OWNER') {
      return true
    }
    
    const permissions = props.loginData?.permissions || []
    return permissions.some((p) => {
      const features = p.features || {}
      return (
        features[PERMISSION_FEATURE.CROSS_BRANCH_INVENTORY] === PERMISSION_TYPE.VIEW ||
        features[PERMISSION_FEATURE.CROSS_BRANCH_INVENTORY] === PERMISSION_TYPE.MANAGE
      )
    })
  }

  // Fetch branches for cross-branch dropdown
  useEffect(() => {
    if (hasCrossBranchPermission()) {
      getBranchHelper()
        .then((res) => {
          // Filter out current branch
          const otherBranches = res.filter(
            (branch) => branch.id !== props.loginData?.branch_id
          )
          setBranches(otherBranches)
        })
        .catch(console.log)
    }
  }, [props.loginData])

  // Fetch cross-branch inventory when branch is selected
  useEffect(() => {
    if (selectedBranch) {
      fetchCrossBranchInventory()
    }
  }, [selectedBranch])

  const fetchCrossBranchInventory = () => {
    if (!selectedBranch) return

    setCrossBranchLoading(true)
    getCrossBranchInventoryHelper({
      branch_id: selectedBranch.id,
      search: crossBranchSearch || '',
    })
      .then((res) => {
        setCrossBranchInventory(res.inventory || [])
      })
      .catch((err) => {
        console.error('Error fetching cross-branch inventory:', err)
        setCrossBranchInventory([])
      })
      .finally(() => {
        setCrossBranchLoading(false)
      })
  }

  const handleCrossBranchSearch = (e) => {
    e.preventDefault()
    fetchCrossBranchInventory()
  }

  const getFilteredCrossBranchInventory = () => {
    if (!crossBranchSearch.trim()) return crossBranchInventory

    const searchTerm = crossBranchSearch.toLowerCase().trim()
    return crossBranchInventory.filter(
      (item) =>
        item.name?.toLowerCase().includes(searchTerm) ||
        item.generic_name?.toLowerCase().includes(searchTerm)
    )
  }

  const handleReminderFilter = (days) => {
    setReminderFilterDays(days)
    pagination.goToPage(1)
  }

  const updateFilterOptions = (newOptions) => {
    setFilterOptions((prev) => ({
      ...prev,
      ...newOptions,
    }))
    pagination.goToPage(1)
  }

  // Pagination hook
  const pagination = usePagination({
    initialPage: 1,
    initialLimit: 20,
  })
  useEffect(() => {
    const getInventoryDetails = async () => {
      setInventoryLoading(true)
      setShowingAlternatives(false)
      setZeroStockProducts([])
      
      try {
        const requestData = {
          ...pagination.getQueryParams(),
          search: filterOptions.search || '',
          type:
            activeTab === INVENTORY_TABS.PHARAMCEUTICAL
              ? 'PHARMACEUTICAL'
              : 'NON_PHARMACEUTICAL',
        }

        // Add expiry filter if reminder filter is active
        if (reminderFilterDays > 0) {
          requestData.expiry_days = reminderFilterDays
        }

        const response = await getInventoryDetailsHelper(requestData)
        const { inventory, pagination: resPagination } = response

        if (!response || !inventory) {
          setInventoryData([])
          return
        }

        // Apply frontend filtering as backup if backend doesn't filter
        let filteredInventory = inventory

        // Filter by type (check both product_type and type fields)
        const expectedType =
          activeTab === INVENTORY_TABS.PHARAMCEUTICAL
            ? 'PHARMACEUTICAL'
            : 'NON_PHARMACEUTICAL'
        filteredInventory = filteredInventory.filter((item) => {
          const itemType = (item.product_type || item.type || '').toUpperCase()
          return itemType === expectedType
        })

        // Filter by search (product name and generic name)
        if (filterOptions.search && filterOptions.search.trim() !== '') {
          const searchTerm = filterOptions.search.toLowerCase().trim()
          
          // First, find products matching the search term
          const searchMatchedProducts = filteredInventory.filter(
            (item) =>
              item.name?.toLowerCase().includes(searchTerm) ||
              item.generic_name?.toLowerCase().includes(searchTerm)
          )
          
          // Check if all matched products have zero stock
          const zeroStockMatches = searchMatchedProducts.filter(
            (item) => !item.quantity || item.quantity === 0
          )
          const inStockMatches = searchMatchedProducts.filter(
            (item) => item.quantity && item.quantity > 0
          )
          
          // If there are zero stock products and no in-stock matches for the search term
          if (zeroStockMatches.length > 0 && inStockMatches.length === 0) {
            // Get unique generic names from zero stock products
            const genericNames = [
              ...new Set(
                zeroStockMatches
                  .map((item) => item.generic_name?.toLowerCase())
                  .filter(Boolean)
              ),
            ]
            
            // Find alternative products with same generic name that have stock
            const alternativeProducts = filteredInventory.filter(
              (item) =>
                item.quantity &&
                item.quantity > 0 &&
                item.generic_name &&
                genericNames.includes(item.generic_name.toLowerCase()) &&
                !zeroStockMatches.some((zp) => zp.id === item.id)
            )
            
            if (alternativeProducts.length > 0) {
              setShowingAlternatives(true)
              setZeroStockProducts(zeroStockMatches)
              // Mark alternative products
              const markedAlternatives = alternativeProducts.map((item) => ({
                ...item,
                isAlternative: true,
              }))
              filteredInventory = [...zeroStockMatches, ...markedAlternatives]
            } else {
              // No alternatives found, show zero stock products
              filteredInventory = zeroStockMatches
            }
          } else {
            // Normal search results (in-stock products found)
            filteredInventory = searchMatchedProducts
          }
        }

        setInventoryData(filteredInventory)

        if (resPagination?.count) pagination.setTotalItems(resPagination.count)
        if (resPagination?.total_pages)
          pagination.setTotalPages(resPagination.total_pages)
      } catch (error) {
        console.error('Error fetching inventory:', error)
        setInventoryData([])
      } finally {
        setInventoryLoading(false)
      }
    }

    getInventoryDetails()
  }, [pagination.page, pagination.limit, filterOptions, activeTab, reminderFilterDays])

  const handleProductListingClick = (product) => {
    setActiveProduct(product)
    getProductDetails(product.id)
  }

  const getProductDetails = (id) => {
    getProductDetailsHelper(id)
      .then((res) => {
        setProductData(res)
        setInventoryStage(INVENTORY_STAGES.PRODUCT_DETAILS)
      })
      .catch(console.log)
  }

  const getInvoiceDetails = (id) => {
    console.log('invoice details: ', id)
    getInvoiceDetailsHelper(id)
      .then((res) => {
        console.log('mc res: ', res)
        setInvoiceData(res)
        handleDrawerToggle()
      })
      .catch((err) => {
        console.log(err)
      })
  }
  const handleDrawerToggle = () => {
    setViewDrawer(!viewDrawer)
    setOpen(!open)
  }

  const handleHeaderConfigUpdate = (title, handleBackFunc) => {
    setHeaderOptions({
      [HEADER_CONFIG.IS_BACK_VISIBLE]: true,
      [HEADER_CONFIG.TITLE]: title,
      [HEADER_CONFIG.HANDLE_BACK_FUNC]: handleBackFunc,
    })
  }

  useEffect(() => {
    if (!open && inventoryStage === INVENTORY_STAGES.PRODUCT_LISTING) {
      handleHeaderConfigUpdate(HEADER_CONST.MANAGE_INVENTORY, () =>
        redirect(navigate, PAGE_CONST.HOME)
      )
    } else if (open) {
      handleHeaderConfigUpdate(HEADER_CONST.VIEW_INVENTORY, () => {
        handleDrawerToggle()
        setInventoryStage(INVENTORY_STAGES.PRODUCT_DETAILS)
      })
    } else if (inventoryStage === INVENTORY_STAGES.PRODUCT_DETAILS) {
      handleHeaderConfigUpdate(HEADER_CONST.VIEW_INVENTORY, () =>
        setInventoryStage(INVENTORY_STAGES.PRODUCT_LISTING)
      )
    }
  }, [open, inventoryStage])

  return (
    <>
      {isMobile && <MobileHeader headerOptions={headerOptions} />}

      <div className="pageBodyRightPanel">
        <InventoryFilter
          filterOptions={filterOptions}
          setFilterOptions={updateFilterOptions}
          inventoryStage={inventoryStage}
          setInventoryStage={setInventoryStage}
          activeTab={activeTab}
          inventoryData={inventoryData}
          onReminderFilter={handleReminderFilter}
          isReminderFilterActive={reminderFilterDays > 0}
        />

        {/* Cross-Branch Inventory Toggle - Only show for users with permission */}
        {hasCrossBranchPermission() && inventoryStage === INVENTORY_STAGES.PRODUCT_LISTING && (
          <div className="crossBranchToggle" style={{ marginBottom: '16px' }}>
            <button
              className={`btn ${showCrossBranchView ? 'btnPrimary' : 'btnSecondary'}`}
              onClick={() => setShowCrossBranchView(!showCrossBranchView)}
              style={{ marginRight: '10px' }}
            >
              {showCrossBranchView ? 'Back to My Inventory' : 'Check Other Branch Inventory'}
            </button>
          </div>
        )}

        {/* Cross-Branch Inventory View */}
        {showCrossBranchView && hasCrossBranchPermission() && (
          <div className="crossBranchInventorySection">
            <div className="crossBranchHeader" style={{ 
              display: 'flex', 
              gap: '16px', 
              marginBottom: '20px',
              flexWrap: 'wrap',
              alignItems: 'flex-end'
            }}>
              {/* Branch Dropdown */}
              <div className="formGroup" style={{ minWidth: '200px', marginBottom: 0 }}>
                <label>Select Branch</label>
                <select
                  className="formControl"
                  value={selectedBranch?.id || ''}
                  onChange={(e) => {
                    const branch = branches.find((b) => b.id === parseInt(e.target.value))
                    setSelectedBranch(branch || null)
                    setCrossBranchSearch('')
                  }}
                >
                  <option value="">-- Select Branch --</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Search Box */}
              {selectedBranch && (
                <div className="formGroup" style={{ flex: 1, minWidth: '250px', marginBottom: 0 }}>
                  <label>Search Product</label>
                  <form onSubmit={handleCrossBranchSearch} style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      className="formControl"
                      placeholder="Search by Product Name or Generic Name"
                      value={crossBranchSearch}
                      onChange={(e) => setCrossBranchSearch(e.target.value)}
                    />
                    <button type="submit" className="btn btnPrimary">
                      Search
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* Cross-Branch Inventory Table */}
            {selectedBranch && (
              <>
                {crossBranchLoading ? (
                  <Loading />
                ) : (
                  <>
                    <div style={{ 
                      backgroundColor: '#f8f9fa', 
                      padding: '12px 16px', 
                      borderRadius: '8px',
                      marginBottom: '16px'
                    }}>
                      <strong>Branch:</strong> {selectedBranch.name}
                      {selectedBranch.address && (
                        <span style={{ marginLeft: '16px', color: '#666' }}>
                          | {selectedBranch.address}
                        </span>
                      )}
                    </div>

                    <div className="tableWrapper" style={{ overflowX: 'auto' }}>
                      <table className="table inventoryTable" style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse',
                        fontSize: '14px'
                      }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f5f5f5' }}>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Branch Name</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Product Name</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Size</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Category</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Origin</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Expiry Date</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Supplier/Manufacturer</th>
                            <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>RPU</th>
                            <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getFilteredCrossBranchInventory().length === 0 ? (
                            <tr>
                              <td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                                {crossBranchSearch 
                                  ? `No products found for "${crossBranchSearch}"` 
                                  : 'No inventory data available for this branch'}
                              </td>
                            </tr>
                          ) : (
                            getFilteredCrossBranchInventory().map((item, index) => (
                              <tr 
                                key={item.id || index} 
                                style={{ 
                                  borderBottom: '1px solid #eee',
                                  backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa'
                                }}
                              >
                                <td style={{ padding: '12px' }}>{selectedBranch.name}</td>
                                <td style={{ padding: '12px' }}>
                                  <div>
                                    <strong>{item.name} {item.dose || ''}</strong>
                                    {item.generic_name && (
                                      <div style={{ fontSize: '12px', color: '#666' }}>
                                        Generic: {item.generic_name}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td style={{ padding: '12px' }}>{item.dose || item.size || '-'}</td>
                                <td style={{ padding: '12px' }}>{item.category || '-'}</td>
                                <td style={{ padding: '12px' }}>{item.origin || '-'}</td>
                                <td style={{ padding: '12px' }}>{item.expiry_date ? formatDate(item.expiry_date) : '-'}</td>
                                <td style={{ padding: '12px' }}>{item.supplier_name || item.manufacturer || '-'}</td>
                                <td style={{ padding: '12px', textAlign: 'right' }}>
                                  {item.rate_per_unit ? currencyFormatter(item.rate_per_unit) : '-'}
                                </td>
                                <td style={{ 
                                  padding: '12px', 
                                  textAlign: 'right',
                                  fontWeight: 'bold',
                                  color: item.quantity > 0 ? '#28a745' : '#dc3545'
                                }}>
                                  {item.quantity || 0}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {getFilteredCrossBranchInventory().length > 0 && (
                      <div style={{ 
                        marginTop: '16px', 
                        padding: '12px', 
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        fontSize: '14px',
                        color: '#666'
                      }}>
                        Showing {getFilteredCrossBranchInventory().length} product(s)
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {!selectedBranch && (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                color: '#666'
              }}>
                <p>Please select a branch to view its inventory</p>
              </div>
            )}
          </div>
        )}

        {/* Normal Inventory View */}
        {!showCrossBranchView && inventoryStage === INVENTORY_STAGES.PRODUCT_LISTING && (
          <ul className="tabs dFlex mt-5">
            <li
              className={
                activeTab === INVENTORY_TABS.PHARAMCEUTICAL
                  ? 'tabItem active'
                  : 'tabItem'
              }
              onClick={() => setActiveTab(INVENTORY_TABS.PHARAMCEUTICAL)}
            >
              Pharmaceutical
            </li>
            <li
              className={
                activeTab === INVENTORY_TABS.NON_PHARMACEUTICAL
                  ? 'tabItem active'
                  : 'tabItem'
              }
              onClick={() => setActiveTab(INVENTORY_TABS.NON_PHARMACEUTICAL)}
            >
              Non Pharmaceutical
            </li>
          </ul>
        )}
        {!showCrossBranchView && inventoryStage === INVENTORY_STAGES.PRODUCT_DETAILS && (
          <div className="itemBased">
            <div className="itemName">
              {formatProductName(activeProduct.name, activeProduct.dose, activeProduct.strength, activeProduct.category)}
            </div>
            <div className="itemDetails">
              <span className="item name">Category: {activeProduct.category || 'N/A'}</span>
              <span className="item type">Balance Qty: {activeProduct.quantity || 0}</span>
            </div>
          </div>
        )}
        {!showCrossBranchView && inventoryStage === INVENTORY_STAGES.PRODUCT_LISTING && (
          <>
            {inventoryLoading && <Loading />}

            {!inventoryLoading && (
              <>
                {showingAlternatives && (
                  <div className="alternativeProductsNotice" style={{
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '16px',
                    color: '#856404'
                  }}>
                    <strong>Note:</strong> The searched product is out of stock. Showing alternative products with the same generic name.
                  </div>
                )}
                
                <div className="productLlistWrap dFlex">
                  {inventoryData?.map((inventory) => {
                    const isOutOfStock = !inventory.quantity || inventory.quantity === 0
                    const isAlternative = inventory.isAlternative
                    
                    return (
                      <div 
                        className="cardWrap" 
                        key={inventory.id}
                        style={{
                          position: 'relative',
                          opacity: isOutOfStock ? 0.7 : 1,
                        }}
                      >
                        {isOutOfStock && (
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            zIndex: 1
                          }}>
                            OUT OF STOCK
                          </div>
                        )}
                        {isAlternative && (
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            zIndex: 1
                          }}>
                            ALTERNATIVE
                          </div>
                        )}
                        <Card
                          headerProduct={{
                            productType: inventory.origin,
                            productCategory: inventory.category,
                            quantity: `Qty: ${inventory.quantity || 0}`,
                          }}
                          noHeaderBorder
                          strong={false}
                          productDesc={`Generic Name: ${inventory.generic_name || 'N/A'}`}
                          headerMain={formatProductName(inventory.name, inventory.dose, inventory.strength, inventory.category)}
                          handleClick={() => handleProductListingClick(inventory)}
                        />
                      </div>
                    )
                  })}
                </div>

                {inventoryData?.length === 0 && filterOptions.search && (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#666'
                  }}>
                    <p>No products found for "{filterOptions.search}"</p>
                  </div>
                )}

                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={pagination.goToPage}
                  onPrevPage={pagination.prevPage}
                  onNextPage={pagination.nextPage}
                  onFirstPage={pagination.firstPage}
                  onLastPage={pagination.lastPage}
                  hasNextPage={pagination.hasNextPage}
                  hasPrevPage={pagination.hasPrevPage}
                  showFirstLast
                  maxPageButtons={5}
                />
              </>
            )}
          </>
        )}

        {!showCrossBranchView && inventoryStage === INVENTORY_STAGES.PRODUCT_DETAILS && (
          <div className="productLlistWrap dFlex">
            {productData?.map((product) => {
              const remainingDays = calculateRemainingDays(product.expiry_date)
              return (
                <div className="cardWrap" key={product.product_receiving_id}>
                  <Card
                    headerSup={`Expiry: ${formatDate(product.expiry_date)} (${remainingDays !== null ? remainingDays + ' days left' : 'N/A'})`}
                    headerProduct={{
                      productType: `Invoice No.: ${product.invoice_number}`,
                      productCategory: `Amount: BDT ${currencyFormatter(parseFloat(product?.amount).toFixed(2))}`,
                      quantity: `Qty: ${product.total_quantity}`,
                      rpu: `RPU: ${product.rate_per_unit ? currencyFormatter(product.rate_per_unit) : 'N/A'}`,
                    }}
                    noHeaderBorder
                    strong={false}
                    headerMain={`${product.pharma_company}`}
                    handleClick={() =>
                      getInvoiceDetails(product.product_receiving_id)
                    }
                  />
                </div>
              )
            })}
          </div>
        )}

        <ViewInventory
          open={open}
          handleDrawerToggle={handleDrawerToggle}
          invoiceData={invoiceData}
        />
      </div>
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    loginData: state.loginDataReducer?.loginData || {},
  }
}

export default connect(mapStateToProps)(ManageInventory)
